import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../api/adminApi";
import type { Workflow, WorkflowFileInfo } from "../../../../../api/types/workflowTypes";

interface Props {
  workflowId: string;
  attachment: WorkflowFileInfo;
  onClose: () => void;
  onDeleted: (updatedWorkflow: Workflow) => void;
}

const deleteAttachment = withErrorHandling(adminApi.deleteWorkflowAttachment);

const DeleteWorkflowAttachmentDialog = ({ workflowId, attachment, onClose, onDeleted }: Props) => {
  const { sendNotificationError } = useNotificationContext();

  const [loading, setLoading] = useState(false);

  const handleDeleteClick = async () => {
    setLoading(true);
    const [updatedWorkflow, error] = await deleteAttachment(workflowId, attachment.fileCatalogId);
    setLoading(false);

    if (error) {
      logError(error, "[DeleteWorkflowAttachmentDialog] deleteWorkflowAttachment");
      sendNotificationError(error.message);
      return;
    }

    onDeleted(updatedWorkflow);
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Delete attachment?</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6">{attachment.fileName}</Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              This file and its citations will be marked as removed. This cannot be undone.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="outlined" color="error" loading={loading} onClick={handleDeleteClick}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteWorkflowAttachmentDialog;
